import {
  Box,
  Button,
  Card,
  Container,
  FormControlLabel,
  Grid,
  Switch,
  Table,
  TableBody,
  TableContainer,
  TablePagination,
} from '@mui/material';
import { useMemo, useState } from 'react';
import Page from 'src/components/Page';
import { LoadingTableRow, TableHeadCustom } from 'src/components/table';
import { PATH_DASHBOARD } from 'src/routes/paths';
import CustomFieldsRow from './CustomFieldsRow';
import HeaderBreadcrumbs from 'src/components/HeaderBreadcrumbs';
import Iconify from 'src/components/Iconify';
import CustomFieldModal from './CustomFieldModal';
import useTable from 'src/hooks/useTable';
import { useStructureDefinitionsFiltered } from 'src/hooks/useStructureDefinitionsFiltered';
import useTenantConfigData from 'src/careflow/ph-core/multi-tenant/tenant/hook/useTenantConfigData';
import useLocales from 'src/careflow/ph-core/internationalization/hook/useLocales';
import useAuth from 'src/careflow/ph-core/authentication/hook/useAuth';
import { WrappedStructureDefinition } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/StructureDefinition';
import { StructureDefinition } from 'src/careflow/ph-core/fhir-client/resources';
import { checkAclValidation } from 'src/careflow/ph-core/admin/permissions/util/permission.utils';
import crsAcls from 'src/careflow/ph-core/admin/permissions/util/crs/crsAcls';
import { translateTableHead } from 'src/careflow/helpers/common';
import { TABLE_HEAD_CUSTOM_FIELD_LIST } from 'src/careflow/common/table-head';
import NoResultsTableRow from 'src/careflow/common/NoResultsTableRow';

export default function CustomFieldsList() {
  const { i18n } = useLocales();
  const authUser = useAuth().getCurrentUser();
  const { componentsData, configurations } = useTenantConfigData();
  const { allUserOrganizations, filterVSByResource } = componentsData?.customFields ?? {};
  const { page, rowsPerPage, onChangePage, onChangeRowsPerPage } = useTable({});

  const [showRetired, setShowRetired] = useState(false);
  const [customFieldModal, setCustomFieldModal] = useState(false);
  const [rowSelected, setRowSelected] = useState<WrappedStructureDefinition | null>(null);

  const {
    structureDefinitionSys,
    structureDefinitionOrg,
    isLoading,
    userOrganizations,
    refresh: refreshStructureDefinition,
    update: updateStructureDefinition,
  } = useStructureDefinitionsFiltered({
    authUser,
    allUserOrganizations,
  });

  const rows = useMemo(
    () =>
      showRetired
        ? structureDefinitionOrg
        : structureDefinitionOrg?.filter((e) => e?.status !== 'retired'),
    [structureDefinitionOrg, showRetired]
  );

  const handler = async (data: StructureDefinition) => {
    await updateStructureDefinition(data);
    refreshStructureDefinition();
  };

  const customFieldsPermissions = useMemo(
    () => ({
      isAllowedToAdd: checkAclValidation({ acls: [crsAcls.ADMIN.CUSTOM_FIELDS.ADD] }),
      isAllowedToDelete: checkAclValidation({ acls: [crsAcls.ADMIN.CUSTOM_FIELDS.DELETE] }),
    }),
    []
  );

  return (
    <>
      <Page title={i18n('customFields.title', 'crs')}>
        <Container maxWidth={'xl'}>
          <Grid item xs={6} alignItems={'center'}>
            <HeaderBreadcrumbs
              title={i18n('customFields.title', 'crs')}
              heading=""
              links={[
                { name: `${i18n('admin.list.dashboard')}`, href: PATH_DASHBOARD.root },
                { name: `${i18n('customFields.title', 'crs')}` },
              ]}
              action={
                customFieldsPermissions?.isAllowedToAdd ? (
                  <Button
                    variant="contained"
                    startIcon={<Iconify icon="eva:plus-fill" />}
                    onClick={() => setCustomFieldModal(true)}
                  >
                    {i18n('customFields.buttonTitleAdd', 'crs')}
                  </Button>
                ) : null
              }
            />
          </Grid>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Card>
                <TableContainer sx={{ py: 2 }}>
                  <Table>
                    <TableHeadCustom
                      headLabel={translateTableHead(TABLE_HEAD_CUSTOM_FIELD_LIST, i18n, 'crs')}
                    />
                    <TableBody>
                      {!isLoading ? (
                        rows?.length ? (
                          rows
                            ?.slice?.(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                            ?.map((row) => (
                              <CustomFieldsRow
                                key={row.id}
                                row={row}
                                handler={() => {
                                  setRowSelected(row);
                                  setCustomFieldModal(true);
                                }}
                              />
                            ))
                        ) : (
                          <NoResultsTableRow
                            colSpan={TABLE_HEAD_CUSTOM_FIELD_LIST.length}
                            text={i18n('customFields.list.listNotFound', 'crs')}
                          />
                        )
                      ) : (
                        <LoadingTableRow colSpan={TABLE_HEAD_CUSTOM_FIELD_LIST.length} />
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
                <Box sx={{ position: 'relative' }}>
                  <TablePagination
                    rowsPerPageOptions={[5, 10, 25]}
                    component="div"
                    count={rows ? rows?.length : 0}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={onChangePage}
                    onRowsPerPageChange={onChangeRowsPerPage}
                  />
                  <FormControlLabel
                    control={
                      <Switch checked={showRetired} onChange={() => setShowRetired(!showRetired)} />
                    }
                    label={i18n('customFields.list.includeRetired', 'crs')}
                    sx={{ px: 3, py: 1.5, top: 0, position: { md: 'absolute' } }}
                  />
                </Box>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Page>
      <CustomFieldModal
        open={customFieldModal}
        systemTemplate={structureDefinitionSys}
        userOrganizationsIds={userOrganizations}
        configurations={configurations?.fhir}
        filterVSByResource={filterVSByResource}
        rowToEdit={rowSelected}
        handlerCreate={handler}
        onClose={() => {
          setRowSelected(null);
          setCustomFieldModal(false);
        }}
      />
    </>
  );
}
